export function isUnknownRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function toUnknownRecord(value: unknown): Record<string, unknown> {
  return isUnknownRecord(value) ? value : {};
}

export function readUnknownProperty(value: unknown, key: string): unknown {
  return isUnknownRecord(value) ? value[key] : undefined;
}

export function isMemberOf<TMember extends string>(
  values: readonly TMember[],
  value: string | null | undefined,
): value is TMember {
  return values.some((candidate) => candidate === value);
}

export function isSetMember<TMember extends string>(
  values: ReadonlySet<TMember>,
  value: string | null | undefined,
): value is TMember {
  for (const candidate of values) {
    if (candidate === value) return true;
  }
  return false;
}

export function isKeyOf<TKey extends string>(
  record: Record<TKey, unknown>,
  key: string | null | undefined,
): key is TKey {
  return (
    typeof key === "string" && Object.prototype.hasOwnProperty.call(record, key)
  );
}

export function lookupKey<TKey extends string, TValue>(
  record: Record<TKey, TValue>,
  key: string | null | undefined,
): TValue | undefined {
  return isKeyOf(record, key) ? record[key] : undefined;
}

export function recordKeys<TKey extends string>(record: Record<TKey, unknown>): TKey[] {
  return Object.keys(record).filter((key): key is TKey => isKeyOf(record, key));
}

function isCompleteRecord<TKey extends string, TValue>(
  keys: readonly TKey[],
  record: Partial<Record<TKey, TValue>>,
): record is Record<TKey, TValue> {
  return keys.every((key) =>
    Object.prototype.hasOwnProperty.call(record, key),
  );
}

export function recordFromKeys<TKey extends string, TValue>(
  keys: readonly TKey[],
  valueOf: (key: TKey) => TValue,
): Record<TKey, TValue> {
  const record: Partial<Record<TKey, TValue>> = {};
  for (const key of keys) {
    record[key] = valueOf(key);
  }
  if (!isCompleteRecord(keys, record)) {
    throw new Error("recordFromKeys produced an incomplete record");
  }
  return record;
}

export function mapRecordValues<TKey extends string, TValue, TResult>(
  record: Record<TKey, TValue>,
  mapValue: (value: TValue, key: TKey) => TResult,
): Record<TKey, TResult> {
  return recordFromKeys(recordKeys(record), (key) =>
    mapValue(record[key], key),
  );
}
